import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Receipt as ReceiptIcon } from '@mui/icons-material';
import { getTalkPlanMasters } from '../api/talkPlanMasters';
import { TalkPlanMaster } from '../types/talkPlanMaster';
import { TimeDisplay, TruncatedText } from '../components/common';
import TalkPlanMasterRegistrationDialog from '../components/talkPlanMaster/TalkPlanMasterRegistrationDialog';
import TalkPlanMasterEditDialog from '../components/talkPlanMaster/TalkPlanMasterEditDialog';
// テキスト表示の最大文字数
const TEXT_MAX_LENGTH = 15;
const REPLY_FREQUENCY_MAX_LENGTH = 10;
const PLAN_NAME_MAX_LENGTH = 10;
const TalkPlanMasterList: React.FC = () => {
  const [ talkPlanMasters, setTalkPlanMasters ] = useState<TalkPlanMaster[]>([]);
  const [ totalCount, setTotalCount ] = useState(0);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  // ページネーション用の状態
  const [ page, setPage ] = useState(0);
  const [ rowsPerPage, setRowsPerPage ] = useState(20);
  // ダイアログの状態
  const [ openRegistrationDialog, setOpenRegistrationDialog ] = useState(false);
  const [ openEditDialog, setOpenEditDialog ] = useState(false);
  const [ selectedTalkPlanMaster, setSelectedTalkPlanMaster ] = useState<TalkPlanMaster | null>(null);
  // データを取得する関数
  const fetchTalkPlanMasters = async() => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTalkPlanMasters({
        page: page + 1,  // APIは1ベース、Material-UIは0ベース
        page_size: rowsPerPage,
        ordering: '-created_at'
      });
      setTalkPlanMasters(data.results);
      setTotalCount(data.count);
    } catch (err) {
      console.error('対話プランデータの取得に失敗しました', err);
      setError('対話プランデータの取得に失敗しました。');
      setTalkPlanMasters([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  // ページ変更時の処理
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };
  // ページサイズ変更時の処理
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // 初期ロードとページ変更時のデータ取得
  useEffect(() => {
    fetchTalkPlanMasters();
  }, [ page, rowsPerPage ]);
  // 価格のフォーマット
  const formatPrice = (price: number) => {
    return `¥${price.toLocaleString()}`;
  };
  // 登録成功時の処理
  const handleRegistrationSuccess = () => {
    fetchTalkPlanMasters();
  };
  // 編集ボタンクリック時の処理
  const handleEditClick = (talkPlanMaster: TalkPlanMaster) => {
    setSelectedTalkPlanMaster(talkPlanMaster);
    setOpenEditDialog(true);
  };
  // 編集成功時の処理
  const handleEditSuccess = () => {
    fetchTalkPlanMasters();
    setOpenEditDialog(false);
    setSelectedTalkPlanMaster(null);
  };
  return (
    <Box sx={{ p: 3 }}>
      {/* ヘッダー部分 */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon/>}
          onClick={() => setOpenRegistrationDialog(true)}
        >
          プラン追加
        </Button>
      </Box>
      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ローディング表示 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress/>
        </Box>
      )}

      {/* テーブル */}
      {!loading && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center">公開状態</TableCell>
                  <TableCell>プラン名</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell align="right">期間(日)</TableCell>
                  <TableCell>返信頻度</TableCell>
                  <TableCell>プラン詳細</TableCell>
                  <TableCell align="right">登録数</TableCell>
                  <TableCell>備考</TableCell>
                  <TableCell>最終更新日時</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {talkPlanMasters.map((planMaster) => (
                  <TableRow key={planMaster.id} hover>
                    <TableCell align="center">
                      {planMaster.published ? (
                        <Chip
                          label="公開"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="非公開"
                          color="default"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <TruncatedText text={planMaster.name} maxLength={PLAN_NAME_MAX_LENGTH}/>
                    </TableCell>
                    <TableCell align="right">
                      {formatPrice(planMaster.price)}
                    </TableCell>
                    <TableCell align="right">
                      {planMaster.period_days}
                    </TableCell>
                    <TableCell>
                      <TruncatedText text={planMaster.reply_frequency} maxLength={REPLY_FREQUENCY_MAX_LENGTH}/>
                    </TableCell>
                    <TableCell>
                      <TruncatedText text={planMaster.description} maxLength={TEXT_MAX_LENGTH}/>
                    </TableCell>
                    <TableCell align="right">
                      {planMaster.talk_plans_count > 0 ? (
                        <Box
                          component={RouterLink}
                          to={`/talk_plan_masters/${planMaster.id}`}
                          sx={{
                            color: 'primary.main',
                            fontWeight: 'bold',
                            fontSize: '1.3rem',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {planMaster.talk_plans_count}
                          <ReceiptIcon sx={{ fontSize: '1rem' }}/>
                        </Box>
                      ) : (
                        <Box sx={{ fontSize: '1.3rem' }}>
                          {planMaster.talk_plans_count}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <TruncatedText text={planMaster.remarks} maxLength={TEXT_MAX_LENGTH}/>
                    </TableCell>
                    <TableCell>
                      <TimeDisplay timestamp={planMaster.updated_at}/>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="medium"
                        onClick={() => handleEditClick(planMaster)}
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.12)',
                            color: 'primary.main',
                          }
                        }}
                      >
                        <EditIcon/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {talkPlanMasters.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body1" sx={{ py: 2 }}>
                        データがありません
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[ 10, 20, 50 ]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) =>
              `${count !== -1 ? count : `${to}以上`}件中 ${from}-${to}`
            }
          />
        </Paper>
      )}

      {/* 登録ダイアログ */}
      <TalkPlanMasterRegistrationDialog
        open={openRegistrationDialog}
        onClose={() => setOpenRegistrationDialog(false)}
        onSuccess={handleRegistrationSuccess}
      />

      {/* 編集ダイアログ */}
      {selectedTalkPlanMaster && (
        <TalkPlanMasterEditDialog
          open={openEditDialog}
          onClose={() => {
            setOpenEditDialog(false);
            setSelectedTalkPlanMaster(null);
          }}
          talkPlanMaster={selectedTalkPlanMaster}
          onSuccess={handleEditSuccess}
        />
      )}
    </Box>
  );
};
export default TalkPlanMasterList;
